// One asset page screen. Words: ONE_ASSET_PAGE_WORDS.md. Mockup: MOCKUP_ONE_ASSET_PAGE.html (approved 2026-10-24).
// Left: the workbook row, top to bottom. Right: where it sits, open records, and the way to change it.
// Pure: view in, HTML out.

import { assetHref, type ARow, type AssetView } from "./asset-page";
import type { ScanState } from "./sites-screens";
import { withAdminBack } from "./admin-way-back";
import { esc, ident } from "./training-screens";

function rowHtml(r: ARow): string {
  const band = r.band ? `<span class="ap-band ${r.band}"></span>` : "";
  const value = r.pill ? `<span class="pill ${r.pill.tone}">${esc(r.pill.text)}</span>` : `${band}<span${r.mono ? ' class="mono"' : ""}>${esc(r.v)}</span>`;
  const sub = r.sub ? `<small>${esc(r.sub)}</small>` : "";
  const end = r.link ? `<a class="lnk" href="${esc(r.link.href)}"${r.link.newTab ? ' target="_blank" rel="noopener"' : ""}>${esc(r.link.text)}</a>` : "";
  return `<div class="r${r.calc ? " ap-calc" : ""}"><div class="k">${esc(r.k)}</div><div class="v">${value}${sub}</div><div class="e">${end}</div></div>`;
}

const card = (title: string, rows: string, small = "") =>
  `<h2 class="hub-sec">${esc(title)}${small ? ` <small>${esc(small)}</small>` : ""}</h2><div class="box"><div class="rows">${rows}</div></div>`;

function scanRows(v: AssetView, scan: ScanState): string {
  const c = scan.count;
  const value =
    c === null ? '<span class="muted">Counting…</span>' : typeof c === "string" ? `<span class="gl-text-bad">${esc(c)}</span>` : `${c.count.toLocaleString("en-US")}<small>${esc(c.line)}</small>`;
  const reset = typeof c === "object" && c ? '<button class="chg" type="button" data-act="scan-reset">Reset Scan Count</button>' : "";
  return `<div class="r"><div class="k">QR Scans</div><div class="v">${value}</div><div class="e">${reset}</div></div>
    <div class="r"><div class="k">Guest Page</div><div class="v"><a class="lnk" href="${withAdminBack(`/?aid=${encodeURIComponent(v.assetId)}`)}" target="_blank" rel="noopener">Open what a scan shows</a></div><div class="e"></div></div>`;
}

function scanSheet(v: AssetView, scan: ScanState): string {
  if (!scan.confirming) return "";
  return `<div class="shade"><div class="sheet" role="dialog" aria-modal="true" aria-label="Reset Scan Count">
    <div class="big">Reset Scan Count</div>
    <p>The QR scan count for ${esc(v.assetId)} starts again from 0 now. The scans already counted stay on record. The QEW page shows the new count.</p>
    ${scan.error ? `<div class="note red">${esc(scan.error)}</div>` : ""}
    <button class="go red" type="button" data-act="scan-reset-go"${scan.busy ? " disabled" : ""}>${scan.busy ? "Resetting…" : "Reset To 0"}</button>
    <button class="go2" type="button" data-act="scan-reset-no"${scan.busy ? " disabled" : ""}>Cancel</button>
  </div></div>`;
}

const node = (site: string, id: string, me = false) =>
  me ? `<span class="ap-node me">${esc(id)}</span>` : `<a class="ap-node" href="${assetHref(site, id)}">${esc(id)}</a>`;

function linkRow(site: string, label: string, ids: string[], none: string): string {
  const body = ids.length ? ids.map((id) => `<a class="mono lnk" href="${assetHref(site, id)}">${esc(id)}</a>`).join(" ") : `<span class="muted">${esc(none)}</span>`;
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v ap-ids">${body}</div><div class="e"></div></div>`;
}

export function assetPageScreen(v: AssetView, scan: ScanState, extra: { canMap: boolean; flash: string }): string {
  const s = v.siteNumber;
  const pills = [v.removed ? '<span class="pill grey">REMOVED</span>' : "", v.outOfService ? '<span class="pill grey">OUT OF SERVICE</span>' : ""].join("");
  const up = v.fedFrom.length ? v.fedFrom : v.sitsIn ? [v.sitsIn] : [];
  const down = [...v.feeds, ...v.waysOut.filter((w) => !v.feeds.includes(w))];
  const chain = `<div class="ap-chain">
      ${up.length ? `<span class="ap-kids">${up.map((id) => node(s, id)).join("")}</span><span class="ap-arrow"></span>` : ""}
      ${node(s, v.assetId, true)}
      ${down.length ? `<span class="ap-arrow"></span><span class="ap-kids">${down.slice(0, 12).map((id) => node(s, id)).join("")}${down.length > 12 ? `<span class="muted">+${down.length - 12} more</span>` : ""}</span>` : ""}
    </div>`;
  const records = v.records.length
    ? v.records.map((r) => `<a class="hub-here" href="${esc(r.href)}"><span class="t"><b>${esc(`${r.kind}${r.priority ? ` · ${r.priority}` : ""}`)}</b><small><span class="mono">${esc(r.id)}</span> ${esc(r.line)}</small></span></a>`).join("")
    : '<div class="empty">None open.</div>';
  const history = v.history.length
    ? v.history.map((h) => `<div class="ap-hist"><b>${esc(h.title)}</b> ${esc(h.detail)}<small>${esc(h.who)}</small></div>`).join("")
    : '<div class="empty">No changes on record.</div>';
  return `<a class="back" href="#/sites/${esc(s)}/assets">Asset List</a>
    ${ident("AS", v.assetId, v.sub)}
    ${pills ? `<div class="ap-pills">${pills}</div>` : ""}
    ${extra.flash}
    <div class="hub-cols">
      <div class="hub-col">
        ${card("Identity", v.identity.map(rowHtml).join(""))}
        ${card("Shock", v.shock.map(rowHtml).join(""))}
        ${card("Arc Flash", v.arcFlash.map(rowHtml).join(""))}
        ${card("Condition", v.condition.map(rowHtml).join(""))}
        ${card("Labels", v.labels.map(rowHtml).join("") + scanRows(v, scan))}
        <h2 class="hub-sec">Change History <small>newest first</small></h2><div class="box">${history}</div>
      </div>
      <div class="hub-col">
        <h2 class="hub-sec">Where It Sits</h2>
        <div class="box">${chain}</div>
        <div class="box"><div class="rows">
          ${v.mapNote ? `<div class="r"><div class="k">Fed From, Feeds</div><div class="v muted">${esc(v.mapNote)}</div><div class="e"></div></div>` : `${linkRow(s, "Fed From", v.fedFrom, "Not mapped yet")}${linkRow(s, "Feeds", v.feeds, "None mapped")}`}
          ${linkRow(s, "Sits In", v.sitsIn ? [v.sitsIn] : [], "Nothing. It is a housing or stands alone.")}
          ${linkRow(s, "Ways Out", v.waysOut, "None")}
        </div></div>
        <h2 class="hub-sec">Open Records</h2>
        <div class="box">${records}</div>
        <div class="box ap-btns">
          <a class="prim" href="#/assets/${esc(s)}/row/${encodeURIComponent(v.assetId)}">Change In Creator</a>
          ${extra.canMap ? `<a class="chg" href="#/survey/${esc(s)}/map">Open On Map</a>` : ""}
        </div>
        <p class="ro">Read only. Changes happen in the Assessment Sheet.</p>
      </div>
    </div>
    ${scanSheet(v, scan)}`;
}
